import { useMemo } from 'react';

import { ENTITLEMENT_USER_LIST_AD_FILTER } from '../../../../../../shared/constants';
import { useGetUsersMutation } from '../../../../../ViolationsTab/data-layer/violationsTabApi';
import { type FiltersGeneralProps } from '../../../../../ViolationsTab/data-layer/violationsTabTypes';

/**
 * Custom hook to generate a query map for advanced filters.
 *
 * This hook retrieves query functions for different advanced filters and
 * returns a memoized object mapping filter keys to their corresponding query functions.
 * Includes wrapper functions for displayname, departmentname, and email filters
 * that pass the correct key parameter to return actual field values instead of userKeys.
 *
 * @param {boolean} [strict=false] - If `true`, only includes the query function which has initial options;
 *                                   if `false`, includes all types of query functions.
 * @returns {Object} Query map where keys are filter values and values are query functions with custom transformations.
 */
const useGetQueryMap = (strict = false) => {
  const [fetchUserFilters] = useGetUsersMutation();

  const queryMap = useMemo(() => {
    const queryFn = {};

    const isEmptyQueryMap = Boolean(Object.keys(queryFn).length === 0);

    if (strict && isEmptyQueryMap) {
      return {
        EMPTY_QUERY_FN_KEY: () => ({
          unwrap() {
            return Promise.resolve('empty_query_fn_run_for_query');
          },
        }),
      };
    }

    if (strict) {
      return queryFn;
    }

    return {
      ...queryFn,
      [ENTITLEMENT_USER_LIST_AD_FILTER.USERNAME.value]: fetchUserFilters,
      [ENTITLEMENT_USER_LIST_AD_FILTER.FIRSTNAME.value]: fetchUserFilters,
      [ENTITLEMENT_USER_LIST_AD_FILTER.LASTNAME.value]: fetchUserFilters,
      [ENTITLEMENT_USER_LIST_AD_FILTER.DISPLAYNAME.value]: (
        params: FiltersGeneralProps
      ) => fetchUserFilters({ ...params, key: 'displayname' }),
      [ENTITLEMENT_USER_LIST_AD_FILTER.DEPARTMENTNAME.value]: (
        params: FiltersGeneralProps
      ) => fetchUserFilters({ ...params, key: 'departmentname' }),
      [ENTITLEMENT_USER_LIST_AD_FILTER.EMAIL.value]: (
        params: FiltersGeneralProps
      ) => fetchUserFilters({ ...params, key: 'email' }),
    };
  }, [strict, fetchUserFilters]);

  return queryMap;
};

export default useGetQueryMap;
